<template>
	<!-- 股东人数 -->
	<MStateBox :loading="initialLoading" :empty="isEmpty" class="act-container" ref="container">
		<van-pull-refresh v-model="refreshLoading" success-text="刷新成功" @refresh="onPullRefresh">
			<div ref="layout">
				<div class="box-wrapper">
					<ShareholderCountChart :xData="date" :yBarData="stockholders" />
				</div>
				<div class="box-hr"></div>
				<div class="box-wrapper">
					<MiniTable :data="finalData">
						<MiniTableColumn prop="date" :label="i18n.t('enterprise.time')" width="92" />
						<MiniTableColumn prop="totalAccount" :label="i18n.t('enterprise.number-of-shareholders')">
							<template #default="{ row }">
								<span class="font-table-barlow">{{ formatterColTransferUnit(row.totalAccount) }}</span>
							</template>
						</MiniTableColumn>
						<MiniTableColumn prop="changeRatio" :label="i18n.t('enterprise.percentage-of-change')">
							<template #default="{ row }">
								<span class="font-table-barlow" :style="calcPercentageStyle(row.changeRatio)">{{
									formatPercentage(row.changeRatio)
								}}</span>
							</template>
						</MiniTableColumn>
						<MiniTableColumn prop="averageHoldings" :label="i18n.t('enterprise.average-holding')">
							<template #default="{ row }">
								<span class="font-table-barlow">{{
									formatterColTransferUnit(row.averageHoldings)
								}}</span>
							</template>
						</MiniTableColumn>
					</MiniTable>
					<FooterLoadPlaceholder :more="hasMoreData" @loadMore="onPullUp" />
				</div>
				<m-visible-tips fixed class="visible-background" />
			</div>
		</van-pull-refresh>
	</MStateBox>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import ShareholderCountChart from '@rebuild/materials/shared/shareholder/ShareholderCountChart.vue';
import { formatterColTransferUnit } from '@/utils/enterprise';
import { getRect } from '@/utils/api';
import { PullRefresh as VanPullRefresh } from 'vant';
import i18n from '@/assets/js/i18n';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { usePaginationMobile } from '@rebuild/hooks/load-more/usePaginationMobile';
import MStateBox from '@rebuild/components/MStateBox.vue';
import { MiniTable, MiniTableColumn } from '@rebuild/components/table';
import FooterLoadPlaceholder from '@rebuild/components/FooterLoadPlaceholder.vue';

const initialLoading = ref(true);

const { shareholder_count } = useModuleData();
const isEmpty = computed(() => !initialLoading.value && finalData.value.length === 0);

// 图表仅取"首次 refresh"的数据；下拉刷新时会更新
const chartData = ref([]);
const hasInitializedChart = ref(false);

const loadData = async () => {
	await shareholder_count.refresh({
		pageStart: currentPage.value - 1,
		pageNum: pageSize.value
	});

	const data = shareholder_count.data;
	const newData = data?.data || [];
	const total = data?.total || 0;
	setData(newData, total);

	// 更新图表数据
	if (!hasInitializedChart.value || currentPage.value === 1) {
		chartData.value = newData;
		hasInitializedChart.value = true;
	}
};

const {
	currentPage,
	pageSize,

	finalData,
	setData,
	hasMoreData,
	onPullDown,
	onPullUp
} = usePaginationMobile({
	bindChange: loadData
});

const refreshLoading = ref(false);
const onPullRefresh = async () => {
	refreshLoading.value = true;
	await onPullDown();
	// 下拉刷新完成后更新图表数据
	chartData.value = shareholder_count.data?.data || [];
	refreshLoading.value = false;
};

const date = computed(() => {
	const data = chartData.value || [];
	return data
		.map(item => item.date)
		.slice(0, 5)
		.reverse();
});
const stockholders = computed(() => {
	const data = chartData.value || [];
	return data
		.map(item => (item.totalAccount / 10000).toFixed(2))
		.slice(0, 5)
		.reverse();
});

function formatPercentage(num) {
	if (num === 0) return i18n.t('enterprise.unchanged');
	if (num > 0) return `+${num}%`;
	return `${num}%`;
}

function calcPercentageStyle(num) {
	if (num > 0) return 'color: #EB5149';
	if (num < 0) return 'color: #44C970';
}

const container = ref(null);
const layout = ref(null);

onMounted(async () => {
	await loadData();
	initialLoading.value = false;

	nextTick(() => {
		const containerRect = getRect(container.value);
		if (layout.value) {
			layout.value.style.minHeight = `${containerRect.height}px`;
		}
	});
});
</script>

<style lang="less" scoped>
@import '@/style/mixin/text-overflow.less';

.act-container {
	height: 100%;
	background: #fff;

	.visible-background {
		background: #fff;
	}

	.box-wrapper {
		padding: 0.16rem 0.14rem;
		background: #fff;
	}
	.box-hr {
		height: 0.1rem;
		background: #f6f7f8;
	}
}
</style>
